/**
 * WASM Setup for AgentShield in Next.js Edge Runtime
 * 
 * This module handles WASM initialization for cryptographic signature verification.
 * Designed to work without top-level await to avoid Next.js middleware issues.
 * 
 * Usage in middleware.ts:
 * ```typescript
 * import { setupWasm } from '@kya-os/agentshield-nextjs/wasm-setup';
 * import { createAgentShieldMiddleware } from '@kya-os/agentshield-nextjs';
 * 
 * export async function middleware(request: NextRequest) {
 *   // Initialize WASM inside the middleware function
 *   await setupWasm();
 *   
 *   const agentShieldMiddleware = createAgentShieldMiddleware({...});
 *   return agentShieldMiddleware(request);
 * }
 * ```
 */

let wasmInitialized = false;
let initPromise: Promise<void> | null = null;
let initAttempted = false;

/**
 * Initialize WASM module for AgentShield
 * 
 * This function:
 * - Loads WASM in production/Edge Runtime for cryptographic verification
 * - Skips WASM in test environments (Jest) automatically
 * - Is safe to call multiple times (idempotent)
 * - Handles errors gracefully with fallback to pattern detection
 * 
 * @returns Promise that resolves when initialization is complete
 */
export async function setupWasm(): Promise<void> {
  // Already initialized, return immediately
  if (wasmInitialized) {
    return;
  }

  // Initialization in progress, return the existing promise
  if (initPromise) {
    return initPromise;
  }

  // Start initialization
  initPromise = doSetupWasm();
  return initPromise;
}

async function doSetupWasm(): Promise<void> {
  // Prevent multiple initialization attempts
  if (initAttempted) {
    return;
  }
  initAttempted = true;

  try {
    // Skip WASM in test environments
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
      wasmInitialized = true;
      return;
    }

    // Check if we're in Node.js runtime (middleware with runtime: 'nodejs')
    const isNodejs = typeof process !== 'undefined' && 
                     typeof require !== 'undefined' &&
                     process.env.NEXT_RUNTIME === 'nodejs';

    if (isNodejs) {
      // We're in Node.js runtime - use fs to load WASM!
      try {
        const { loadWasmNodejs } = await import('./nodejs-wasm-loader');
        const loaded = await loadWasmNodejs();
        wasmInitialized = true;
        
        if (loaded) {
          console.log('🚀 AgentShield: Running with full WASM support (Node.js runtime)');
        } else {
          console.log('📊 AgentShield: Using pattern detection (WASM not found)');
        }
        return;
      } catch (error) {
        console.warn('⚠️ AgentShield: Node.js WASM loader failed:', error);
      }
    }

    // Edge Runtime or build time - skip WASM loading
    // Pattern detection will be used (85% confidence)
    wasmInitialized = true;
    
    if (process.env.NEXT_RUNTIME === 'edge') {
      console.log('⚡ AgentShield: Edge Runtime detected - using pattern detection (85% confidence)');
      console.log('✅ AgentShield: Ready for AI agent detection');
    }
  } catch (error) {
    // Mark as initialized to prevent retries
    wasmInitialized = true;
  }
}

/**
 * Check if WASM has been initialized
 * 
 * @returns true if WASM setup has been attempted (success or failure)
 */
export function isWasmInitialized(): boolean {
  return wasmInitialized;
}

/**
 * Reset WASM initialization state (mainly for testing)
 * 
 * @internal
 */
export function resetWasmState(): void {
  wasmInitialized = false;
  initPromise = null;
}